<template>
  <div v-if="groupedRecords.length">
    <hr style="margin: 32px 0">
    <FormInfo gap="10">
      <p class="forminfo-title">事件行銷紀錄 （{{ records.length }}）</p>
      <div class="flex flex-col gap-[10px]">
        <GrayBlockContainer v-for="(group, idx) in groupedRecords" :key="idx" class="!p-2">
          <div class="group-header" @click="toggleGroup(idx)">
            <FlexLayout align="center" :gap="20">
              <Tag :type="getStatusConfig(group.status).type">
                {{ getStatusConfig(group.status).label }}
              </Tag>
              <span class="font-medium">{{ group.eventName }}</span>
            </FlexLayout>
            <MaterialIcon color="var(--gray-60)" size="20" :class="['chevron', { 'is-expanded': expandedGroups[idx] }]">keyboard_arrow_down</MaterialIcon>
          </div>

          <el-collapse-transition>
            <div v-show="expandedGroups[idx]" class="group-body">
              <HistoryRecord
                :history-data="group.logs"
                :get-step-status="getStepStatus"
              >
                <template #title="{ record: log }">
                  <div class="timeline-title">
                    <span class="font-bold text-sub text-gray-100 tracking-[1px]">
                      {{ getActionConfig(log.action).label }} {{ getAwardTitle(log) }}
                    </span>
                    <span v-if="getLogAmount(log)" :class="['text-sub', getLogAmount(log).color]">
                      {{ getLogAmount(log).text }}
                    </span>
                  </div>
                </template>
                <template #default="{ record: log }">
                  <div class="timeline-content">
                    <p class="text-gray-60 text-sub tracking-[1px]">
                      <template v-if="log.origin === 'system'">
                        系統自動<template v-if="log.originType || log.originId">，</template>{{ getOriginTypeLabel(log.originType) }} <template v-if="log.originId">{{ log.originType ? ' ' : '' }}<a class="underline cursor-pointer" @click.prevent="$emit('goTransaction', { type: 'id', value: log.originId })">{{ log.originId }}</a></template>
                      </template>
                      <template v-else-if="log.origin === 'user'">
                        手動回收<template v-if="log.note">，{{ log.note }}</template>
                      </template>
                      <template v-else>
                        {{ log.note || '-' }}
                      </template>
                    </p>
                    <p v-if="log.errorMessage" class="text-danger text-sub">{{ log.errorMessage }}</p>
                  </div>
                </template>
              </HistoryRecord>
            </div>
          </el-collapse-transition>
        </GrayBlockContainer>
      </div>
    </FormInfo>
  </div>
</template>

<script>
import { computed, defineComponent, reactive, set } from 'vue'
import { find, get, groupBy, map } from 'lodash'
import { eventPlaybookAwardRecordStatusConfig, eventAwardConfig } from '@/config/marketing'
import { awardRecordLogActionConfig, awardTypeUnitConfig, awardRecordLogOriginTypeConfig } from '@/config/eventPlaybookAwardRecordLog'
import FormInfo from '@/components/FormInfo/FormInfo.vue'
import Tag from '@/components/Tag/Tag.vue'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import HistoryRecord from '@/components/HistoryRecord/HistoryRecord.vue'
import MaterialIcon from '@/components/MaterialIcon.vue'

export default defineComponent({
  name: 'EventMarketingRewardBlock',
  components: { FormInfo, Tag, GrayBlockContainer, HistoryRecord, MaterialIcon },
  props: {
    records: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['goTransaction'],
  setup (props) {
    const expandedGroups = reactive({})

    const groupedRecords = computed(() => {
      const grouped = groupBy(props.records, (r) => get(r, 'EventPlaybookId') || get(r, 'EventPlaybook.id'))
      return map(grouped, (items) => {
        const first = items[0]
        const logs = items.reduce((acc, item) => {
          const itemLogs = get(item, 'EventPlaybookAwardRecordLogs', [])
          const awardType = get(item, 'awardType')
          const awardName = get(item, 'awardName')
          return acc.concat(itemLogs.map(log => ({ ...log, awardType, awardName })))
        }, [])
        logs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        return {
          eventName: get(first, 'EventPlaybook.name', '-'),
          status: get(first, 'status', 'sent'),
          logs,
        }
      })
    })

    const toggleGroup = (idx) => {
      set(expandedGroups, idx, !expandedGroups[idx])
    }

    const getStatusConfig = (status) => {
      return get(eventPlaybookAwardRecordStatusConfig, status, { label: status, type: 'info' })
    }

    const getActionConfig = (action) => {
      return get(awardRecordLogActionConfig, action, { label: action })
    }

    const getAwardTypeLabel = (awardType) => {
      const config = find(eventAwardConfig, { value: awardType })
      return get(config, 'label', '')
    }

    const getAwardTitle = (log) => {
      const awardTypeLabel = getAwardTypeLabel(log.awardType)
      const awardName = get(log, 'awardName')
      return awardName ? `${awardTypeLabel} - ${awardName}` : awardTypeLabel
    }

    const getLogAmount = (log) => {
      const unit = get(awardTypeUnitConfig, log.awardType, '')
      if (log.action === 'sent' || log.action === 'sendFailed') {
        const amount = get(log, 'content.awardAmount')
        if (amount != null) return { text: `+${amount} ${unit}`, color: 'text-primary-100' }
      }
      if (log.action === 'revoked' || log.action === 'revokeFailed') {
        const amount = get(log, 'content.actualRevokeAmount')
        if (amount != null) return { text: `-${amount} ${unit}`, color: 'text-danger' }
      }
      return null
    }

    const getOriginTypeLabel = (originType) => {
      if (!originType) return '消費'
      return get(awardRecordLogOriginTypeConfig, `${originType}.label`, '消費')
    }

    const getStepStatus = (log) => {
      const config = get(awardRecordLogActionConfig, log.action)
      return get(config, 'stepStatus', 'finish')
    }

    return {
      groupedRecords,
      expandedGroups,
      toggleGroup,
      getStatusConfig,
      getActionConfig,
      getAwardTitle,
      getAwardTypeLabel,
      getLogAmount,
      getOriginTypeLabel,
      getStepStatus,
    }
  },
})
</script>

<style lang="postcss" scoped>
.forminfo-title {
  @apply text-primary-100 font-medium text-normal;
}

.group-header {
  @apply flex items-center justify-between cursor-pointer select-none;
}

.chevron {
  @apply transition-transform duration-200;
}

.chevron.is-expanded {
  transform: rotate(180deg);
}

.group-body {
  @apply pt-3;
}

.timeline-content {
  @apply flex flex-col gap-1;
}

.timeline-title {
  @apply flex items-center justify-between w-full;
}
</style>
